import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import { groupsApi } from '../../../services/groups-api-client';
import { mutationErrorMessage } from '../../../lib/mutation-error';
import { HANDED_OFF_NOTE } from '../../../lib/broadcastDelivery';
import { plural } from '../../../lib/plural';
import {
  EMAIL,
  SMS,
  sendOutcomeSentence,
  normalizeChannels,
  summarizeBroadcastAudience,
  toggleChannel,
  totalSendable,
  type BroadcastChannel,
} from '../../../lib/broadcastChannels';
import { AUDIENCE_ALL, audienceSegment } from '../broadcasts/audience';
import ChannelPicker from '../broadcasts/ChannelPicker';
import GuestPreviewPanel from './GuestPreviewPanel';
import { ChannelPlanSummary, ResultList } from '../broadcasts/BroadcastPlanSummary';
import { BROADCAST_HISTORY_KEY } from '../broadcasts/BroadcastHistory';
import type { ChangeNoticeDraft } from '../../../lib/changeNotice';
import type { GroupDetail, SendGroupBroadcastRequest, SendGroupBroadcastResponse } from '../../../types/groups';

const SUBJECT_MAX = 200;
const BODY_MAX = 5000;

type Stage = 'ask' | 'compose';

/**
 * Who a notice goes to when that is NOT everyone. A dinner's notice belongs to
 * the guests holding a seat at it, not to the whole roster.
 *
 * Explicit invitee ids on the wire, exactly what BroadcastsTab sends for an RSVP
 * audience. The list must never be empty: an empty `inviteeIds` reads as "no id
 * filter", which the API resolves to EVERYONE — so an empty audience blocks the
 * send instead of widening it.
 */
export interface NoticeAudience {
  inviteeIds: string[];
  /** "the 6 guests with seats at this dinner". */
  label: string;
  /** Something the host should know the notice will NOT reach. */
  note?: string | null;
}

/**
 * The prompt the owner asked for, verbatim: "ask the host if they want to send
 * an update when a change is made vs. someone accidentally changing something
 * then the whole group auto getting a blast." ItineraryTab opens this right
 * AFTER a save has already succeeded and lib/changeNotice.ts decided guests
 * would notice — never before, and never as a side effect of typing.
 *
 * This dialog never sends on its own. The default, autofocused action is
 * "Don't send"; closing it any other way — Escape, the backdrop, the browser
 * back button tearing down the page — resolves to exactly the same thing,
 * because MUI's Dialog `onClose` fires for all three and every one of them is
 * wired to `dontSend`. Reaching an actual send costs two more explicit steps:
 * "Send an update…" opens the prefilled, editable draft (it does not send —
 * the label says where the host is headed, and the draft is one click away),
 * and Send stays disabled until a
 * dry-run preview has been run against the CURRENT draft (the same
 * dry-run/preview mechanism BroadcastsTab uses, reused rather than
 * reimplemented) and states the guest count it is about to send to.
 */
export default function ChangeNoticeDialog({
  open,
  onClose,
  group,
  notice,
  audience,
  undoHint = 'edit the tile back',
}: {
  open: boolean;
  onClose: () => void;
  group: GroupDetail;
  notice: ChangeNoticeDraft | null;
  /** Omitted means all guests, which is what an itinerary change wants. */
  audience?: NoticeAudience;
  /**
   * How the host takes the change back, completing "If this was a mistake, just …". Defaults to
   * the itinerary-tile wording; a change that has no tile (the trip dates, the time zone, a dinner)
   * says what it actually is ("change the dates back").
   */
  undoHint?: string;
}) {
  const [stage, setStage] = useState<Stage>('ask');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [smsBody, setSmsBody] = useState('');
  // Email only until the host says otherwise, same posture as the broadcast
  // composer: nobody gets texted because a dialog opened.
  const [channels, setChannels] = useState<BroadcastChannel[]>([EMAIL]);
  const [personalizeInviteLinks, setPersonalizeInviteLinks] = useState(
    () => group.accessMode === 'invite_only',
  );
  const [preview, setPreview] = useState<SendGroupBroadcastResponse | null>(null);
  const [previewKey, setPreviewKey] = useState('');
  const [sendResult, setSendResult] = useState<SendGroupBroadcastResponse | null>(null);
  // Snapshot of what the completed send actually ran on — read back for the
  // confirmation line rather than the live `channels`, which the host cannot
  // change any more but which React has no reason to freeze on its own.
  const [sentChannels, setSentChannels] = useState<BroadcastChannel[]>([EMAIL]);

  const queryClient = useQueryClient();
  const mutation = useMutation(
    (request: SendGroupBroadcastRequest) => groupsApi.sendBroadcast(group.id, request),
  );
  const sending = mutation.isLoading;
  const error = mutationErrorMessage(mutation.error);

  // Reset to a fresh "ask" screen only on the transition from closed to open —
  // not on every render while it is already open, which would blow away a
  // host's half-written draft the instant they touched a field.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current && notice) {
      setStage('ask');
      setSubject(notice.subject);
      setBody(notice.emailBody);
      setSmsBody(notice.smsText);
      setChannels([EMAIL]);
      // Same invite-only default the initial useState uses. Without this, a
      // host who flipped this off for one notice would find it still off the
      // next time this dialog opens for an unrelated change.
      setPersonalizeInviteLinks(group.accessMode === 'invite_only');
      setPreview(null);
      setPreviewKey('');
      setSendResult(null);
      mutation.reset();
    }
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, notice]);

  if (!notice) return null;

  const smsSelected = channels.includes(SMS);
  const smsBodyError = smsSelected && !smsBody.trim()
    ? 'Write the text message before sending by SMS.'
    : null;

  // Re-arms exactly like the broadcast composer: any edit to the draft, the
  // channels, or the link-personalization choice changes this key, which
  // disarms Send until a fresh dry run confirms the numbers again.
  const composeKey = JSON.stringify([subject, body, smsBody, channels, personalizeInviteLinks, audience?.inviteeIds]);
  const armed = Boolean(preview) && previewKey === composeKey;
  const planAudience = armed && preview ? summarizeBroadcastAudience(preview, channels) : null;
  const plans = planAudience?.plans ?? [];
  const sendable = totalSendable(plans);
  const unreported = planAudience?.unreported ?? [];

  const audienceLabel = audience ? audience.label : 'all guests';
  // An empty id list would be read by the API as "everyone" — never send it.
  const audienceEmpty = Boolean(audience) && audience!.inviteeIds.length === 0;
  // The dry run says how many guests the API resolved. More than the ids we
  // asked for means the audience was widened somewhere; refuse rather than send.
  const audienceOverreach = Boolean(audience && preview && armed)
    && preview!.requestedCount > audience!.inviteeIds.length;
  const audienceBlocked = audienceEmpty || audienceOverreach;

  const composeReady = Boolean(subject.trim())
    && Boolean(body.trim())
    && channels.length > 0
    && !smsBodyError;

  const buildRequest = (dryRun: boolean): SendGroupBroadcastRequest => ({
    subject,
    body,
    channels: normalizeChannels(channels),
    smsBody: smsSelected ? smsBody : null,
    // Everyone who can see this tile, resolved exactly the way the broadcast
    // composer resolves "All guests" (segment: null → no filter) — unless the
    // caller narrowed it. A dinner notice carries the claimants' ids and no
    // segment, so it can only ever reach the people holding a seat.
    segment: audienceSegment(AUDIENCE_ALL),
    inviteeIds: audience ? audience.inviteeIds : undefined,
    personalizeInviteLinks,
    dryRun,
  });

  const handlePreview = async () => {
    if (audienceEmpty) return;
    setSendResult(null);
    try {
      const result = await mutation.mutateAsync(buildRequest(true));
      setPreview(result);
      setPreviewKey(composeKey);
    } catch {
      // react-query exposes the failure through mutation.error; mutationErrorMessage renders it.
    }
  };

  const handleSend = async () => {
    // The only path to dryRun:false in this component. `armed` requires a
    // preview keyed to the exact draft on screen, so there is no way to reach
    // this line without having previewed the message the host is about to send.
    if (!armed) return;
    // Belt and suspenders for a narrowed audience: never widen it, never send it empty.
    if (audienceBlocked) return;
    try {
      const result = await mutation.mutateAsync(buildRequest(false));
      setSentChannels(normalizeChannels(channels));
      setSendResult(result);
      setPreview(null);
      setPreviewKey('');
      queryClient.invalidateQueries(BROADCAST_HISTORY_KEY(group.id));
    } catch {
      // Compose state is left exactly as it was — the host can fix whatever
      // failed and retry without retyping the message.
    }
  };

  const dontSend = () => {
    setStage('ask');
    onClose();
  };

  const sentAudience = sendResult ? summarizeBroadcastAudience(sendResult, sentChannels) : null;

  return (
    <Dialog open={open} onClose={dontSend} fullWidth maxWidth="sm">
      {stage === 'ask' || sendResult ? (
        <>
          <DialogTitle>Let guests know about this change?</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1.5 }}>{notice.summary}</Typography>
            {audience ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                If you send an update, it goes only to {audience.label}.
              </Typography>
            ) : null}
            {audience?.note ? <Alert severity="warning" sx={{ mb: 1.5 }}>{audience.note}</Alert> : null}
            {sendResult && sentAudience ? (
              <Alert severity={sentAudience.plans.some((plan) => plan.sent > 0) ? 'success' : 'warning'}>
                {sendOutcomeSentence(sentAudience.plans)} · {plural(sendResult.requestedCount, 'guest')} targeted.
                <Typography variant="caption" display="block">{HANDED_OFF_NOTE}</Typography>
              </Alert>
            ) : (
              <Alert severity="info">
                Nobody has been told about this yet — Studio never sends an update by itself. If this
                was a mistake, just {undoHint}; there is nothing on the guest side to undo.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            {sendResult ? (
              <Button variant="contained" autoFocus onClick={onClose}>Close</Button>
            ) : (
              <>
                <Button variant="text" onClick={() => setStage('compose')} disabled={!group.published}>
                  Send an update…
                </Button>
                {/* The primary, autofocused action. Enter or a stray click lands here. */}
                <Button variant="contained" autoFocus onClick={dontSend}>Don&apos;t send</Button>
              </>
            )}
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Review the update</DialogTitle>
          <DialogContent>
            {!group.published ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Publish this group before Studio can send guests anything.
              </Alert>
            ) : null}
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {audience ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                This goes only to {audience.label}.{audience.note ? ` ${audience.note}` : ''}
              </Alert>
            ) : null}
            {audienceEmpty ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Nobody can be messaged about this — sending is blocked rather than widened to everyone.
              </Alert>
            ) : null}
            {audienceOverreach ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                The preview reached more guests than were meant to get this. Sending stays blocked.
              </Alert>
            ) : null}
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value.slice(0, SUBJECT_MAX))}
                inputProps={{ maxLength: SUBJECT_MAX }}
                helperText={`${subject.length}/${SUBJECT_MAX}`}
              />
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Email message"
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
                inputProps={{ maxLength: BODY_MAX }}
                helperText={`${body.length}/${BODY_MAX} · Variables: {name} {title} {location} {url}`}
              />
              <ChannelPicker
                channels={channels}
                onToggle={(channel, on) => setChannels((prev) => toggleChannel(prev, channel, on))}
                smsBody={smsBody}
                onSmsBody={setSmsBody}
                smsBodyError={smsBodyError}
                disabled={sending}
                personalizeInviteLinks={personalizeInviteLinks}
                onPersonalizeInviteLinks={setPersonalizeInviteLinks}
                inviteOnly={group.accessMode === 'invite_only'}
                trip={{ groupId: group.id, title: group.title, slug: group.slug, locationName: group.locationName }}
              />
            </Stack>
            {notice.smsWarning && smsSelected && smsBody === notice.smsText ? (
              <Alert severity="warning" sx={{ mt: 2 }}>{notice.smsWarning}</Alert>
            ) : null}
            <GuestPreviewPanel
              group={group}
              subject={subject}
              emailBody={body}
              smsBody={smsBody}
              emailSelected={channels.includes(EMAIL)}
              smsSelected={smsSelected}
              personalizeInviteLinks={personalizeInviteLinks}
              guest={audience ? group.invitees?.find((invitee) => invitee.id === audience.inviteeIds[0]) ?? null : undefined}
            />
            {preview && armed ? (
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Preview · {plural(preview.requestedCount, 'guest')} targeted ({audienceLabel})
                </Typography>
                {unreported.length > 0 ? (
                  <Alert severity="warning">
                    The concierge API reported no per-channel result for part of this send — sending
                    stays blocked until it does.
                  </Alert>
                ) : null}
                <ChannelPlanSummary plans={plans} />
                <ResultList results={preview.results} />
              </Stack>
            ) : null}
            {preview && !armed ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                The message changed since the last preview. Run another dry run to re-arm sending.
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStage('ask')}>Back</Button>
            <Button variant="outlined" disabled={sending || !composeReady} onClick={handlePreview}>
              {sending ? 'Working…' : 'Preview (dry run)'}
            </Button>
            <Button
              variant="contained"
              disabled={sending || !armed || !group.published || sendable === 0 || unreported.length > 0 || audienceBlocked}
              onClick={handleSend}
            >
              {/* Before a dry run there is no honest count to put on this button. */}
              {armed ? `Send to ${preview!.requestedCount} guest${preview!.requestedCount === 1 ? '' : 's'}` : 'Send'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
